import { FormEventHandler } from "react"
import { Link, useForm } from "@inertiajs/react"
import { AppLayout } from "../../components/AppLayout"
import { Badge } from "../../components/Badge"
import { CardEditorNav } from "../../components/CardEditorNav"
import { Field } from "../../components/Field"
import { SectionCard } from "../../components/SectionCard"
import { TextInput } from "../../components/TextInput"
import { dayNames } from "../../utils/hebrew"

type OpeningHour = { day_of_week: number | "" ; opens_at: string; closes_at: string; is_closed: boolean; position: number }

type Props = {
  card: { id: number; title: string; slug: string; public_url: string; preview_url: string; opening_hours_hidden: boolean; opening_hours: OpeningHour[] }
  errors: Record<string, string>
  customer_mode?: boolean
}

export default function OpeningHours({ card, customer_mode: customerMode = false }: Props) {
  const openingHoursPath = customerMode ? `/dash/card/${card.id}/opening-hours` : `/admin/cards/${card.id}/opening_hours`
  const editUrl = customerMode ? `/dash/card/${card.id}/edit` : `/admin/cards/${card.id}/edit`
  const form = useForm({
    opening_hours_hidden: card.opening_hours_hidden,
    opening_hours: card.opening_hours.length > 0 ? card.opening_hours : Array.from({ length: 7 }, (_, day_of_week) => ({ day_of_week, opens_at: "", closes_at: "", is_closed: false, position: day_of_week })),
  })
  const { data, setData, patch, processing } = form

  const updateHour = (index: number, field: keyof OpeningHour, value: string | boolean) => {
    const next = [...data.opening_hours]
    next[index] = { ...next[index], [field]: field === "day_of_week" ? (value === "" ? "" : Number(value)) : value } as OpeningHour
    setData("opening_hours", next)
  }

  const submit: FormEventHandler = (event) => {
    event.preventDefault()
    patch(openingHoursPath, { preserveScroll: true })
  }

  return (
    <AppLayout title={`${card.title} | שעות פתיחה`} customerMode={customerMode}>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2"><h1 className="text-3xl font-bold">שעות פתיחה</h1><Badge tone="slate">{card.slug}</Badge></div>
          <p className="text-slate-600">הגדירו מתי העסק פתוח ומה יוצג בכרטיס הציבורי.</p>
        </div>
        <div className="flex flex-wrap gap-2"><Link href={card.preview_url} className="btn-secondary">תצוגה</Link><Link href={editUrl} className="btn-secondary">חזרה לעריכה</Link></div>
      </div>

      <CardEditorNav cardId={card.id} customerMode={customerMode} activeSection="opening_hours" analyticsHref={customerMode ? `/dash/card/${card.id}/analytics` : `/admin/cards/${card.id}/analytics`} />

      <form onSubmit={submit} className="space-y-6">
        <SectionCard title="שעות הפעילות" description="השאירו יום סגור או הגדירו שעות פתיחה וסגירה.">
          <div className="space-y-3">
            {data.opening_hours.map((hour, index) => (
              <div key={index} className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 md:grid-cols-[1.2fr_1fr_1fr_auto] md:items-end">
                <label><span className="label-base">יום</span><select className="input-base" value={hour.day_of_week} onChange={(event) => updateHour(index, "day_of_week", event.target.value)}>{dayNames.map((day, dayIndex) => <option key={day} value={dayIndex}>{day}</option>)}</select></label>
                <Field label="פתיחה"><TextInput type="time" value={hour.opens_at} onChange={(event) => updateHour(index, "opens_at", event.target.value)} disabled={hour.is_closed} /></Field>
                <Field label="סגירה"><TextInput type="time" value={hour.closes_at} onChange={(event) => updateHour(index, "closes_at", event.target.value)} disabled={hour.is_closed} /></Field>
                <label className="flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-3"><input type="checkbox" checked={hour.is_closed} onChange={(event) => updateHour(index, "is_closed", event.target.checked)} /><span className="text-sm text-slate-700">סגור</span></label>
              </div>
            ))}
          </div>
        </SectionCard>
        <SectionCard title="הצגה בכרטיס" description="אפשר להסתיר את שעות הפתיחה בלי למחוק אותן.">
          <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3"><input type="checkbox" checked={!data.opening_hours_hidden} onChange={(event) => setData("opening_hours_hidden", !event.target.checked)} /><span className="text-sm text-slate-700">הצג שעות פתיחה בכרטיס הציבורי</span></label>
        </SectionCard>
        <button type="submit" className="btn-primary bg-brand-600" disabled={processing}>שמירת שעות הפתיחה</button>
      </form>
    </AppLayout>
  )
}
